// src/components/UI.jsx
import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Map, MessageCircle, BookOpen, CalendarCheck, User, Wine } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ROAD_BADGE = {
  "ar-valley":     { bg: "rgba(107,39,55,0.1)",  color: "#886070", border: "rgba(107,39,55,0.2)"  },
  "vienna-venice": { bg: "rgba(122,106,85,0.1)", color: "#7A6A55", border: "rgba(122,106,85,0.2)" },
  "malopolska":    { bg: "rgba(74,112,82,0.1)",  color: "#4A7052", border: "rgba(74,112,82,0.2)"  },
};

const ROAD_LABELS = {
  "ar-valley":     "Ar Valley",
  "vienna-venice": "Vienna – Venice",
  "malopolska":    "Małopolska",
};

export function RoadBadge({ slug }) {
  const s = ROAD_BADGE[slug] || { bg: "#EAEFE2", color: "#666", border: "#B8C8A0" };
  return (
    <span className="road-badge" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      <Wine size={9} />{ROAD_LABELS[slug] || slug}
    </span>
  );
}

export function TopBar({ title, right, back }) {
  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 40,
      background: "rgba(242,245,236,0.96)", backdropFilter: "blur(10px)",
      borderBottom: "1px solid #C4D0A8", paddingTop: "env(safe-area-inset-top)",
    }}>
      <div style={{ display: "flex", alignItems: "center", height: 52, padding: "0 16px", gap: 10 }}>
        {back && (
          <button onClick={back} style={{
            padding: 6, marginLeft: -6, borderRadius: 7,
            background: "transparent", border: "none", color: "#6A7E58", cursor: "pointer",
            display: "flex", transition: "color 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.color="#2C3820"}
          onMouseLeave={e => e.currentTarget.style.color="#6A7E58"}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </button>
        )}
        <h1 style={{
          flex: 1, fontFamily: "'Playfair Display', serif",
          fontSize: 17, fontWeight: 600, color: "#1E2C14",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: 0,
        }}>{title}</h1>
        {right}
      </div>
    </header>
  );
}

// Regular single-destination tabs. "Plan" itself is no longer one of
// these — it's rendered as a single toggle button that shows whichever
// mode you're currently in and switches to the other on tap (see
// PlanTabToggle below), not two side-by-side sub-tabs.
const TABS = [
  { to: "/tours",    icon: BookOpen,      label: "My Tours"    },
  { to: "/bookings", icon: CalendarCheck, label: "My Bookings" },
  { to: "/account",  icon: User,          label: "Account"     },
];

function PlanTabToggle() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth("traveler");
  const onChat = location.pathname === "/chat";
  const onPlanOrChat = onChat || location.pathname === "/plan";
  const isActive = onPlanOrChat;
  // While already on /plan or /chat, this is a toggle between the two —
  // tapping it switches modes, same as before. Arriving from anywhere
  // else (My Tours, My Bookings, Account) is a plain "go to Plan" tap,
  // not a mode switch — it must land on whichever mode the traveler's
  // account preference says, not silently default to chat every time
  // (see AccountPage.jsx's interaction_method setting, the only place
  // this preference is actually changed).
  const preferredIsChat = (user?.interaction_method || "form") === "chat";
  const Icon = onPlanOrChat ? (onChat ? MessageCircle : Map) : (preferredIsChat ? MessageCircle : Map);
  const label = onPlanOrChat ? (onChat ? "Chat" : "Form") : (preferredIsChat ? "Chat" : "Form");
  const target = onPlanOrChat ? (onChat ? "/plan" : "/chat") : (preferredIsChat ? "/chat" : "/plan");
  return (
    <button type="button" onClick={() => navigate(target)} style={{
      flex: 1, background: "none", border: "none", cursor: "pointer",
    }}>
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
        padding: "9px 0", color: isActive ? "#886070" : "#2E3D48",
        fontSize: 10, fontWeight: 500, transition: "color 0.15s",
      }}>
        <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
        <span>{label}</span>
      </div>
    </button>
  );
}

export function BottomNav() {
  const location = useLocation();
  return (
    <nav style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 40,
      background: "rgba(20,20,20,0.97)", backdropFilter: "blur(10px)",
      borderTop: "1px solid #222222", paddingBottom: "env(safe-area-inset-bottom)",
    }}>
      <div style={{ display: "flex" }}>
        <PlanTabToggle />
        {TABS.map(({ to, icon: Icon, label }) => {
          const isActive = location.pathname === to;
          return (
            <NavLink key={to} to={to} style={{ flex: 1, textDecoration: "none" }}>
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                padding: "9px 0", color: isActive ? "#886070" : "#2E3D48",
                fontSize: 10, fontWeight: 500, transition: "color 0.15s",
              }}>
                <Icon size={20} strokeWidth={isActive ? 2 : 1.5} />
                <span>{label}</span>
              </div>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

export function LoadingDots() {
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", padding: "0 4px" }}>
      {[0,1,2].map(i => (
        <span key={i} className="animate-pulse-soft" style={{
          width: 5, height: 5, borderRadius: "50%", background: "#2E3D48",
          animationDelay: `${i * 0.2}s`, display: "block",
        }} />
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      textAlign: "center", padding: "64px 32px", gap: 16,
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: "50%", background: "#EDF1E4",
        border: "1px solid #222", display: "flex", alignItems: "center", justifyContent: "center", color: "#2E3D48",
      }}>
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <div>
        <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 600, color: "#1E2C14", margin: "0 0 4px" }}>{title}</p>
        <p style={{ fontSize: 13, color: "#666", margin: 0 }}>{body}</p>
      </div>
      {action}
    </div>
  );
}

export function ErrorMsg({ message }) {
  if (!message) return null;
  return (
    <div style={{
      background: "rgba(107,39,55,0.08)", border: "1px solid rgba(107,39,55,0.2)",
      color: "#886070", borderRadius: 7, padding: "10px 14px", fontSize: 13,
    }}>
      {message}
    </div>
  );
}
